import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select, { components } from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import ReactDOM from "react-dom";

import {
    useFormInput,
    comma,
    uncomma,
    dateFormatDateTime,
    dateFormatToISOString,
    formatNumberWithCommas,
    formatDecimalInput,
    formatIdNumber,
    onlyNumericInput,
    formatTime,
    formatDate,
} from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { usePostCarMutation, usePutCarMutation, useLazySearchCarQuery, useGetCarByIdQuery, useDeleteCarMutation } from "@/redux/carApi";
import { useGetMyRentCorpQuery, useGetBranchInfoQuery } from "@/redux/rentCorpApi";
import { useGetCarLocationListQuery } from "@/redux/carLocationApi";
import { usePostParkingLotMutation } from "@/redux/parkingLotApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import { originOptions, memberStatusOptions, rolesOptions, mobileTypeOptions } from "@/constants/selectOptions";
import { useGetBankListQuery } from "@/redux/metaApi";
import { useUpdateMyInfoMutation, useChangePasswordMutation } from "@/redux/memberApi";
import { useNicePhoneRequestMutation, useNicePhoneVerifyMutation } from "@/redux/authApi";
import { AuthRequestStatus, AuthCodeStatus } from "@/enum";

// 비밀번호 초기화 팝업 컴포넌트
const ResetPasswordPopup = ({ onClose }) => {
    const [nicePhoneRequest] = useNicePhoneRequestMutation();
    const [nicePhoneVerify] = useNicePhoneVerifyMutation();
    const [changePassword] = useChangePasswordMutation();
    const timerRef = useRef(null);

    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        authRequestStatus: AuthRequestStatus.AUTH_NUM_REQUESTED_BEFORE,
        authCodeStatus: AuthRequestStatus.WAITING,
        timer: 0,
        name: "",
        mobileType: "",
        phone: "",
        idNumber: "",
        authNum: "",
        phoneAuthenticationId: null,
        password: "",
        passwordCheck: "",
    });

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    const startTimer = useCallback(() => {
        if (formData.authCodeStatus !== AuthCodeStatus.IN_PROGRESS || timerRef.current) return;

        timerRef.current = setInterval(() => {
            setFormData((prev) => {
                const newTimer = prev.timer - 1;
                if (newTimer <= 0) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return { ...prev, authCodeStatus: AuthCodeStatus.TIMEOUT, timer: 0 };
                }
                return { ...prev, timer: newTimer };
            });
        }, 1000);
    }, [formData.authCodeStatus]);

    useEffect(() => {
        if (formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS) {
            startTimer();
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [formData.authCodeStatus, startTimer]);

    const handleRequestAuth = async () => {
        if (formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        try {
            const resp = await nicePhoneRequest({
                idNumber: formData.idNumber?.substring(0, 7), // 주민번호 앞 7자리만 전달 (6자리 + 뒷자리 첫번째)
                mobileType: formData.mobileType,
                phone: formData.phone,
                name: formData.name,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                phoneAuthenticationId: resp.data?.id || 0,
                toastMsg: "인증번호가 전송되었습니다.",
                authRequestStatus: AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER,
                authCodeStatus: AuthCodeStatus.IN_PROGRESS,
                timer: 180,
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "인증번호 요청에 실패했습니다.",
            }));
        }
    };

    const handleVerifyAuth = async () => {
        try {
            await nicePhoneVerify({
                id: formData.phoneAuthenticationId,
                authNum: formData.authNum,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                authRequestStatus: AuthRequestStatus.AUTH_NUM_VERIFIED,
                toastMsg: "본인 인증이 완료되었습니다.",
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "인증에 실패했습니다.",
            }));
        }
    };

    const validatePassword = (password, passwordCheck) => {
        if (!password) return "비밀번호를 입력해주세요.";
        if (password !== passwordCheck) return "비밀번호가 일치하지 않습니다.";

        const hasLetter = /[a-zA-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < 8 || !hasLetter || !hasNumber || !hasSpecial) {
            return "비밀번호는 영문, 숫자, 특수문자를 모두 포함한 8자 이상으로 설정해주세요.";
        }
        return "";
    };

    const handleResetPassword = async () => {
        const errMsg = validatePassword(formData.password, formData.passwordCheck);
        console.log(errMsg);
        if (errMsg) {
            setFormData((prev) => ({ ...prev, toastMsg: errMsg }));
            return;
        }

        try {
            await changePassword({ password: formData.password }).unwrap();
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호가 성공적으로 변경되었습니다.",
            }));
            onClose();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.message || "비밀번호 변경에 실패했습니다.",
            }));
        }
    };

    const inputs = [
        {
            name: "name",
            label: "이름",
            placeholder: "이름을 입력하세요",
            value: formData.name,
            onChange: handleChange,
        },
        {
            name: "idNumber",
            label: "주민등록번호",
            placeholder: "주민등록번호 앞 7자리",
            value: formatIdNumber(formData.idNumber),
            onChange: handleChange,
            onInput: onlyNumericInput,
            maxLength: 7,
        },
    ];

    inputs.push({
        name: "phone",
        label: "휴대폰 번호",
        placeholder: "휴대폰 번호",
        value: formData.phone,
        onChange: handleChange,
        onInput: onlyNumericInput,
    });

    if (formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER) {
        inputs.push({
            name: "authNum",
            label: "인증번호",
            placeholder: "인증번호 입력",
            value: formData.authNum,
            onChange: handleChange,
            onInput: onlyNumericInput,
            rightElement: <span className="timer">{formData.timer > 0 ? formatTime(formData.timer) : "만료"}</span>,
        });
    }

    return ReactDOM.createPortal(
        <>
            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}

            <div className="layer active" onClick={onClose}></div>

            <div className="popup input-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>비밀번호 초기화</span>
                    </div>
                    <div className="content-description">
                        <p>
                            {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_VERIFIED
                                ? "새로운 비밀번호를 입력해주세요."
                                : "비밀번호를 초기화하기 위해 본인인증이 필요합니다."}
                        </p>
                    </div>
                    <div className="content-inputs">
                        {formData.authRequestStatus !== AuthRequestStatus.AUTH_NUM_VERIFIED && (
                            <>
                                {/* 이름 */}
                                <div className="input-group">
                                    <label>이름</label>
                                    <div className="input-wrapper">
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            placeholder="이름을 입력하세요"
                                        />
                                    </div>
                                </div>

                                {/* 주민등록번호 */}
                                <div className="input-group">
                                    <label>주민등록번호</label>
                                    <div className="input-wrapper">
                                        <input
                                            type="text"
                                            name="idNumber"
                                            value={formatIdNumber(formData.idNumber)}
                                            onChange={handleChange}
                                            placeholder="주민등록번호"
                                            maxLength={8}
                                            onInput={onlyNumericInput}
                                        />
                                    </div>
                                </div>

                                {/* 통신사 */}
                                <div className="select-group">
                                    <label>통신사</label>
                                    <Select
                                        className="filter-select"
                                        classNamePrefix="filter-select"
                                        value={mobileTypeOptions.find((opt) => opt.value === formData.mobileType)}
                                        onChange={(option) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                mobileType: option.value,
                                            }))
                                        }
                                        options={mobileTypeOptions}
                                        placeholder="통신사"
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </div>

                                {/* 휴대폰 번호 */}
                                <div className="input-group">
                                    <label>휴대폰 번호</label>
                                    <div className="input-wrapper">
                                        <input
                                            type="text"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="휴대폰 번호"
                                            onInput={onlyNumericInput}
                                            maxLength={11}
                                        />
                                    </div>
                                </div>

                                {/* 인증번호 */}
                                {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER && (
                                    <div className="input-group">
                                        <label>인증번호</label>
                                        <div className="input-wrapper">
                                            <input
                                                type="text"
                                                name="authNum"
                                                value={formData.authNum}
                                                onChange={handleChange}
                                                placeholder="인증번호 입력"
                                                onInput={onlyNumericInput}
                                            />
                                            <span className="timer">{formData.timer > 0 ? formatTime(formData.timer) : "만료"}</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* 비밀번호 입력 필드들 */}
                        {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_VERIFIED && (
                            <>
                                <div className="input-group">
                                    <label>새 비밀번호</label>
                                    <div className="input-wrapper">
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="새 비밀번호 입력"
                                        />
                                    </div>
                                </div>
                                <div className="input-group">
                                    <label>비밀번호 확인</label>
                                    <div className="input-wrapper">
                                        <input
                                            type="password"
                                            name="passwordCheck"
                                            value={formData.passwordCheck}
                                            onChange={handleChange}
                                            placeholder="비밀번호 확인"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    {formData.authRequestStatus !== AuthRequestStatus.AUTH_NUM_VERIFIED ? (
                        <>
                            {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_BEFORE && (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleRequestAuth}
                                    disabled={!formData.name || !formData.idNumber || !formData.mobileType || !formData.phone}
                                >
                                    인증번호 요청
                                </button>
                            )}
                            {formData.authRequestStatus === AuthRequestStatus.AUTH_NUM_REQUESTED_AFTER && (
                                <button className="btn btn-primary" onClick={handleVerifyAuth} disabled={!formData.authNum}>
                                    인증하기
                                </button>
                            )}
                        </>
                    ) : (
                        <button
                            className="btn btn-primary"
                            onClick={handleResetPassword}
                            disabled={!formData.password || !formData.passwordCheck}
                        >
                            비밀번호 변경
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    );
};

export const Mypage = () => {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const [updateMyInfo] = useUpdateMyInfoMutation();
    const [resetPasswordPopupVisible, setResetPasswordPopupVisible] = useState(false);
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();
    const fileInputRefs = {
        BIZ_LIC: useRef(null),
        BANK: useRef(null),
        CORP_SEAL: useRef(null),
    };

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "READ" / "UPDATE"
        isLoading: false,
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,
        // postCar API 필드
        emeyeId: null,
        rentCorpId: null,
        branchId: null,
        parkingLotId: null,
        carLocationId: null,
        ownerName: "",
        carNumber: "",
        vehicleOrigin: "",
        carGubun: "",
        manufacturer: "",
        model: "",
        detailModel: "",
        carClass: "",
        carTrim: "",
        fuel: "",
        transmission: "",
        motorType: "",
        bmNo: undefined,
        bmName: "",
        boiNo: undefined,
        boiName: "",
        boNo: undefined,
        boName: "",
        bsNo: undefined,
        bsName: "",
        bdNo: undefined,
        bdName: "",
        year: 0,
        color: "",
        displacement: 0,
        mileage: 0,
        newPrice: "",
        gasAmount: "",
        washStatus: "",
        roles: "",
        vin: "",
        carLocationName: "",
        parkingLotStatus: "" /* "DIRECT", "SELECT" */,
        parkingLotAddYn: "N",
        parkingLotName: "",
        options: [],
        parkingLotBCode: "",
        parkingLotAddress: "",
        parkingLotAddressDetail: "",
        memo: "",
        inspectRangeFrom: "",
        inspectRangeTo: "",
        firstDate: "",
        carStatus: "PENDING",
        carUploads: [
            {
                uploadType: "REGI",
                uploadId: null,
            },
            {
                uploadType: "INSURANCE",
                uploadId: null,
            },
            {
                uploadType: "ESTIMATE",
                uploadId: null,
            },
        ],
        regiFileName: "",
        insuranceFileName: "",
        estimateFileName: "",
        // 은행 정보 필드
        bankOwner: "",
        bankName: "",
        bankAccount: "",
    });
    const { mode, isLoading, toastMsg, confirmStatus, addressPopupStatus, id } = formData;

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    const {
        data: carDetailData,
        isLoading: isCarDetailLoading,
        isFetching: isCarDetailFetching,
        error: carDetailError,
    } = useGetCarByIdQuery(memberId, {
        skip: !memberId,
    });

    // 모드 설정: memberId 있으면 "UPDATE"
    useEffect(() => {
        if (memberId) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    // 회원 상세조회 API 데이터 매핑
    useEffect(() => {
        if (carDetailData && carDetailData.data) {
            const response = carDetailData.data;

            // setFormData((prev) => ({
            //     ...prev,
            //     isLoading: false,
            //     ...response,
            //     mileage: comma(response.mileage),
            //     displacement: comma(response.displacement),
            //     newPrice: comma(response.newPrice),
            // }));
        }
    }, [carDetailData, setFormData]);

    // API 호출
    const [postCar, { isLoading: isPostLoading }] = usePostCarMutation();
    const [putCar, { isLoading: isPutLoading }] = usePutCarMutation();
    const [postParkingLot] = usePostParkingLotMutation();
    const [doSearchCar, { data: searchCarData, isFetching, error }] = useLazySearchCarQuery();
    const [deleteCar] = useDeleteCarMutation();

    const handleDeleteConfirm = async () => {
        if (!formData?.id) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "삭제할 회원이 없습니다.",
            }));
            return;
        }

        try {
            await deleteCar(formData?.id).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "회원 정보 삭제",
                    confirmDescription: "회원이 성공적으로 삭제되었습니다.",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => {
                                navigate("/vehicle/manage");
                            },
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 회원 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    useEffect(() => {
        if (memberId) {
            setFormData((prev) => ({
                ...prev,
                mode: "READ",
                id: memberId,
            }));
        }
    }, [memberId, setFormData]);

    const handleSave = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "정보 수정",
                confirmDescription: "입력하신 정보로 수정하시겠습니까?",
                confirmButtons: [
                    {
                        label: "예",
                        className: "btn-primary",
                        onClick: async () => {
                            setFormData((prev) => ({
                                ...prev,
                                isLoading: true,
                                confirmStatus: { confirmPopupStatus: false },
                            }));

                            try {
                                const { bankOwner, bankName, bankAccount } = formData;
                                await updateMyInfo({
                                    bankOwner,
                                    bankName,
                                    bankAccount,
                                }).unwrap();

                                setFormData((prev) => ({
                                    ...prev,
                                    isLoading: false,
                                    toastMsg: "정보가 성공적으로 수정되었습니다.",
                                }));
                            } catch (error) {
                                setFormData((prev) => ({
                                    ...prev,
                                    isLoading: false,
                                    toastMsg: error.message || "정보 수정 중 오류가 발생했습니다.",
                                }));
                            }
                        },
                    },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev) => ({
                                ...prev,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    const handleFileChange = async (e, uploadType) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);

            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;

            setFormData((prev) => {
                const updated = [...(prev.rentCorpUploads || [])];
                const idx = updated.findIndex((x) => x.uploadType === uploadType);
                if (idx >= 0) {
                    updated[idx] = { ...updated[idx], uploadId: newUploadId, fileName: fileObj.name };
                } else {
                    updated.push({ uploadId: newUploadId, uploadType, fileName: fileObj.name });
                }
                return {
                    ...prev,
                    rentCorpUploads: updated,
                };
            });
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const handleButtonClick = (type) => {
        fileInputRefs[type].current.click();
    };

    const getFileName = (uploadType) => {
        const uploadItem = formData?.rentCorpUploads?.find((item) => item.uploadType === uploadType);
        return uploadItem?.fileName || "";
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">마이페이지</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode !== "CREATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    회원 삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-outline" onClick={() => setResetPasswordPopupVisible(true)}>
                                비밀번호 초기화
                            </button>
                            {/* <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button> */}
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">기본 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <MemberInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            // handleSearchCar={handleSearchCar}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사업자 선택</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <CorpInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div> */}

                            {/* 첨부파일 섹션 */}
                            {/* <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">첨부파일</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <div className="item-row" data-grow="2">
                                            <div className="row-head">
                                                <span>사업자등록증</span>
                                            </div>
                                            <div className="row-content">
                                                <div className="form-element">
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        value={getFileName("BIZ_LIC")}
                                                        placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                                                        disabled
                                                    />
                                                </div>
                                                <div className="form-element_button">
                                                    <button
                                                        type="button"
                                                        className="btn-form_element btn-outline"
                                                        onClick={() => handleButtonClick("BIZ_LIC")}
                                                    >
                                                        파일첨부
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.BIZ_LIC}
                                                        style={{ display: "none" }}
                                                        accept=".pdf,.jpg,.png,.jpeg"
                                                        onChange={(e) => handleFileChange(e, "BIZ_LIC")}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="item-row" data-grow="2">
                                            <div className="row-head">
                                                <span>통장사본</span>
                                            </div>
                                            <div className="row-content">
                                                <div className="form-element">
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        value={getFileName("BANK")}
                                                        placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                                                        disabled
                                                    />
                                                </div>
                                                <div className="form-element_button">
                                                    <button
                                                        type="button"
                                                        className="btn-form_element btn-outline"
                                                        onClick={() => handleButtonClick("BANK")}
                                                    >
                                                        파일첨부
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.BANK}
                                                        style={{ display: "none" }}
                                                        accept=".pdf,.jpg,.png,.jpeg"
                                                        onChange={(e) => handleFileChange(e, "BANK")}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="item-row" data-grow="2">
                                            <div className="row-head">
                                                <span>법인인감</span>
                                            </div>
                                            <div className="row-content">
                                                <div className="form-element">
                                                    <input
                                                        type="text"
                                                        readOnly
                                                        value={getFileName("CORP_SEAL")}
                                                        placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                                                        disabled
                                                    />
                                                </div>
                                                <div className="form-element_button">
                                                    <button
                                                        type="button"
                                                        className="btn-form_element btn-outline"
                                                        onClick={() => handleButtonClick("CORP_SEAL")}
                                                    >
                                                        파일첨부
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.CORP_SEAL}
                                                        style={{ display: "none" }}
                                                        accept=".pdf,.jpg,.png,.jpeg"
                                                        onChange={(e) => handleFileChange(e, "CORP_SEAL")}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div> */}

                            {/* <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계좌 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BankInfo
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            // handleSearchCar={handleSearchCar}
                                        />
                                    </div>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            parkingLotAddress: data.address,
                            parkingLotBCode: data.bcode,
                        }))
                    }
                />
            )}

            {/* 비밀번호 초기화 팝업 */}
            {resetPasswordPopupVisible && (
                <div className="layer">
                    <ResetPasswordPopup onClose={() => setResetPasswordPopupVisible(false)} />
                </div>
            )}
        </>
    );
};

/* 기본 정보 */
const MemberInfo = ({ formData, handleChange, setFormData, handleSearchCar }) => {
    const { ownerName, vehicleOrigin, color, roles } = formData;

    return (
        <>
            {/* 로그인 ID */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>로그인 ID</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            {/* 회원상태 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>회원상태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={memberStatusOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder=""
                            value={memberStatusOptions.find((opt) => opt.value === vehicleOrigin)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, vehicleOrigin: option.value }))}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            {/* 권한 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>권한</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={rolesOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="권한 선택"
                            value={rolesOptions.find((opt) => opt.value === roles)}
                            onChange={(selectedOptions) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    roles: selectedOptions.map((option) => option.value).join(","),
                                }));
                            }}
                            isMulti
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            {/* 이름 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>이름</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="" value={color || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            {/* 연락처 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>연락처</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="" value={color || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            {/* 주민등록번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>주민등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="" value={color || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            {/* 포인트 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>포인트</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="color" placeholder="" value={color || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>
            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>
        </>
    );
};

const BankInfo = ({ formData, handleChange, setFormData }) => {
    const { bankName, bankOwner, bankAccount } = formData;

    // API 호출: 은행리스트 조회
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
              value: bank.code,
              label: bank.name,
              icon: bank.imgUrl,
          }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>예금주</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="bankOwner"
                            placeholder="예금주를 입력하세요"
                            value={bankOwner || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>은행</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={bankOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="은행 선택"
                            value={bankOptions.find((opt) => opt.value === bankName)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bankName: option.value,
                                }))
                            }
                            components={{ Option: IconOption, SingleValue: CustomSingleValue }}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>계좌번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="bankAccount"
                            placeholder="계좌번호를 입력하세요"
                            value={bankAccount || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const CorpInfo = ({ formData, handleChange, setFormData }) => {
    const { rentCorpId, branchId, carLocationId, carStatus, ownerName } = formData;

    // API 호출: 나의 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData && myRentCorpData.data
            ? myRentCorpData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: branchData } = useGetBranchInfoQuery(rentCorpId, {
        skip: !rentCorpId,
    });
    const branchOptions =
        branchData && branchData.data
            ? branchData.data.map((item) => ({
                  value: item.id,
                  label: item.corpName,
              }))
            : [];

    const { data: carLocationData } = useGetCarLocationListQuery();
    const carLocationOptions =
        carLocationData && carLocationData.data
            ? carLocationData.data.map((item) => ({
                  value: item.id,
                  label: item.name,
              }))
            : [];

    return (
        <>
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 구분</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={branchOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 구분"
                            value={branchOptions.find((opt) => opt.value === branchId)}
                            onChange={(option) => setFormData((prev) => ({ ...prev, branchId: option.value }))}
                            isDisabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>법인 등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 등록번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대표자명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>업태</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>종목</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="ownerName" placeholder="" value={ownerName || ""} onChange={handleChange} disabled />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>개업연월일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="firstDate"
                            value={formatDate(formData?.firstDate)}
                            placeholder=""
                            onChange={handleChange}
                            maxLength={10}
                            disabled
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>

            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>사업장 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ width: "200px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="parkingLotBCode"
                            value={formData?.parkingLotBCode}
                            onChange={handleChange}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                            disabled
                        />
                    </div>
                    <div className="form-element" style={{ width: "668px" }}>
                        <input
                            type="text"
                            placeholder=""
                            name="parkingLotAddress"
                            value={formData?.parkingLotAddress}
                            onChange={handleChange}
                            readOnly
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                            disabled
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            placeholder="상세 주소를 입력해주세요."
                            name="parkingLotAddressDetail"
                            value={formData?.parkingLotAddressDetail}
                            onChange={handleChange}
                            disabled
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const MemoInfo = ({ formData, handleChange, setFormData }) => {
    const { memo } = formData;

    return (
        <>
            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="메모를 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};
